<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    @update:modelValue="updateOtherMailboxDrawerVisible"
    direction="right"
    width="600px"
    :title="isEditing ? 'Edit Mailbox' : 'Other Mailbox'"
  >
    <a-form>
      <div class="form_col">
        <span>Email*</span>
        <a-input allow-clear v-model:value="formState.email" placeholder="Enter email" />
      </div>

      <div class="form_col mt-4">
        <span>Username*</span>
        <a-input
          v-model:value="formState.username"
          placeholder="Enter Username"
          allow-clear
        />
      </div>

      <div class="form_col mt-4">
        <span>Password*</span>
        <a-input-password
          v-model:value="formState.password"
          placeholder="Enter password"
        />
      </div>

      <div class="font-semibold text-lg mt-6">Incoming mail settings</div>

      <div class="flex flex-col">
        <div class="flex w-full mt-4 items-end">
          <div class="form_col flex-1">
            <span>IMAP Server*</span>
            <a-input
              v-model:value="formState.imapHost"
              placeholder="Enter Imap Server"
              allow-clear
            />
          </div>
          <div class="mx-2">
            <a-input-number
              v-model:value="formState.imapPort"
              :min="1"
              :max="65535"
              style="width: 80px"
              placeholder="Port"
            />
          </div>
        </div>
      </div>

      <div class="font-semibold text-lg mt-6">Outbound mail settings</div>

      <div class="flex flex-col">
        <div class="flex w-full mt-4 items-end">
          <div class="form_col flex-1">
            <span>SMTP Server*</span>
            <a-input
              v-model:value="formState.smtpHost"
              placeholder="Enter SMTP Server"
              allow-clear
            />
          </div>
          <div class="mx-2">
            <a-input-number
              v-model:value="formState.smtpPort"
              :min="1"
              :max="65535"
              style="width: 80px"
              placeholder="Enter SMTP Port"
            />
          </div>
        </div>
      </div>
    </a-form>
    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button class="secondary_medium" @click="onCancel">Cancel</a-button>
        <a-button class="primary_medium" @click="onFinish" :loading="loading">
          {{ isEditing ? "Update Mailbox" : "Add Mailbox" }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>
<script setup lang="ts">
import IntegrationService from "@/src/api/IntegrationService.js";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import { message } from "ant-design-vue";
import { onMounted, reactive, ref, watch } from "vue";
import DashboardService from "../api/DashboardService.js";

const props = defineProps({
  drawerVisible: { type: Boolean, required: true },
  emailAccountId: { type: Number, default: null },
  isTeamMailbox: { type: Boolean, default: false },
});

interface FormState {
  email?: string;
  username?: string;
  password?: string;
  imapHost?: string;
  imapPort?: number;
  smtpHost?: string;
  smtpPort?: number;
}

const formState = reactive<FormState>({});

const emit = defineEmits([
  "update:isOtherMailboxDrawerVisible",
  "smtpMailBoxAdded",
]);

const updateOtherMailboxDrawerVisible = (value) => {
  emit("update:isOtherMailboxDrawerVisible", value);
};

const emitSmtpMailBoxAdded = () => {
  emit("smtpMailBoxAdded");
};

const internalDrawerVisible = ref(props.drawerVisible);
const isEditing = ref(false);
const loading = ref(false);

onMounted(async () => {
  setWithPrefilledValues();
  if (props.emailAccountId) {
    isEditing.value = true;
    const response = await IntegrationService.getSmtpDetails(
      props.emailAccountId,
    );

    const smtpDetails = response.data.smtp_details.account;

    formState.smtpHost = smtpDetails.SmtpHost;
    formState.smtpPort = smtpDetails.SmtpPort;
    formState.imapHost = smtpDetails.ImapHost;
    formState.imapPort = smtpDetails.ImapPort;
    formState.email = smtpDetails.Email;
    formState.username = smtpDetails.SmtpUsername;
    formState.password = smtpDetails.SmtpPassword;
    console.log(formState);
  }
});

const setWithPrefilledValues = () => {
  // formState.smtpHost = "smtp.gmail.com";
  formState.smtpPort = 587;
  // formState.imapHost = "imap.gmail.com";
  formState.imapPort = 993;
};

const validateForm = () => {
  if (!formState.email) {
    message.error("Email is required");
    return false;
  }

  if (!formState.username) {
    message.error("Username is required");
    return false;
  }

  if (!formState.password) {
    message.error("Password is required");
    return false;
  }

  if (!formState.imapHost) {
    message.error("IMAP Server is required");
    return false;
  }

  if (!formState.imapPort) {
    message.error("IMAP Port is required");
    return false;
  }

  if (!formState.smtpHost) {
    message.error("SMTP Server is required");
    return false;
  }

  if (!formState.smtpPort) {
    message.error("SMTP Port is required");
    return false;
  }

  return true;
};

const onFinish = async () => {
  const isValid = validateForm();
  if (!isValid) return;
  
  loading.value = true;
  
  try {
    if (isEditing.value) {
      const response = await IntegrationService.updateSmtpDetails(
        props.emailAccountId,
        {
          email: formState.email,
          username: formState.username,
          password: formState.password,
          imapHost: formState.imapHost,
          imapPort: formState.imapPort,
          smtpHost: formState.smtpHost,
          smtpPort: formState.smtpPort,
        },
      );

      if (response.success) {
        message.success("Mailbox updated successfully");
        updateOtherMailboxDrawerVisible(false);
        emitSmtpMailBoxAdded();
      } else {
        if (response.error) {
          message.error(response.error);
        } else {
          message.error("Failed to update mailbox");
        }
      }
    } else {
      const response = await DashboardService.addMailboxes(
        [
          {
            email: formState.email,
            username: formState.username,
            password: formState.password,
            imapHost: formState.imapHost,
            imapPort: formState.imapPort,
            smtpHost: formState.smtpHost,
            smtpPort: formState.smtpPort,
          },
        ],
        props.isTeamMailbox,
      );

      if (response.success) {
        message.success("Mailbox added successfully");
        updateOtherMailboxDrawerVisible(false);
        emitSmtpMailBoxAdded();
      } else {
        if (response.error) {
          message.error(response.error);
        } else {
          message.error("Failed to add mailbox");
        }
      }
    }
  } catch (error) {
    console.error("Error during API call:", error);
    message.error("An unexpected error occurred");
  } finally {
    loading.value = false;
  }
};

const onCancel = () => {
  updateOtherMailboxDrawerVisible(false);
};

watch(
  () => props.drawerVisible,
  (newVal) => {
    if (newVal) setWithPrefilledValues();
  },
);
</script>
